import React, { useContext, useMemo } from 'react';
import styled, { keyframes } from 'styled-components';
import { Trans } from 'react-i18next';
import { useField } from 'react-final-form';
import {
  Header1SemiBold,
  Header3SemiBold,
  Header1,
  SelectField as BaseSelectField,
  header1,
  FlexMiddle
} from '@ubeya/shared-web/components';
import useIndustries from '@ubeya/shared/hooks/generic/useIndustries';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';

const Title = styled(Header1SemiBold)`
  margin-bottom: 18px;
  color: ${({ theme }) => theme.colors.black};
`;

const SubTitle = styled(Header3SemiBold)`
  margin-bottom: 50px;
  color: ${({ theme }) => theme.colors.black};
`;

const slideIn = keyframes`
	0% { transform: translateX(350px); }
	100% { transform:translateX(0); }
`;

const Row = styled(FlexMiddle)`
  ${header1};
  margin: 15px 0;
  flex-wrap: wrap;
  animation: ${slideIn} 200ms;
  color: ${({ theme }) => theme.colors.black};
`;

const Text = styled(Header1)`
  line-height: 45px;
`;

const SelectField = styled(BaseSelectField).attrs(() => ({ searchable: false }))`
  .select {
    border: none;
    border-bottom: 1px solid ${({ theme }) => theme.colors.grey2};
    box-shadow: none;

    .select-label,
    .select-input {
      ${header1};
      padding: 0 10px;
      height: 25px;
      max-width: 300px;
    }

    .select-container {
      cursor: pointer;
    }

    .select-container[data-is-empty='false'] {
      padding-right: 0;
    }

    .select-container[data-is-empty='false'] {
      color: ${({ theme, odd }) => (odd ? theme.colors.primary : theme.colors.greenPositive)};
      ~ .select-arrow {
        display: none;
      }
    }
  }
`;

const SIZE_OPTIONS = [
  { label: '1-49', value: '1-49' },
  { label: '50-199', value: '50-199' },
  { label: '200-499', value: '200-499' },
  { label: '500-999', value: '500-999' },
  { label: '1000+', value: '1000+' }
];

const ROLE_OPTIONS = [
  { label: 'the owner', value: 1 },
  { label: 'in charge of schedules', value: 2 },
  { label: 'a team manager', value: 3 },
  { label: 'a senior director', value: 4 },
  { label: 'an HR professional', value: 5 },
  { label: 'an Operations professional', value: 6 },
  { label: 'a Legal professional ', value: 7 },
  { label: 'a Finance professional', value: 8 }
];

const YourCompany = ({ showTitle = false }) => {
  const { t } = useContext(ConfigContext);
  const { industriesOptions } = useIndustries();

  const {
    input: { value: industryId }
  } = useField('industryId');

  const industry = useMemo(() => industriesOptions.find(({ value }) => value === industryId), [
    industriesOptions,
    industryId
  ]);

  const {
    input: { value: size }
  } = useField('size');

  // const {
  //   input: { value: role }
  // } = useField('role');

  // const {
  //   input: { value: members }
  // } = useField('members');

  // const {
  //   input: { value: memberTypes }
  // } = useField('memberTypes');

  const prefix = useMemo(() => {
    if (industry?.category === 'industry_category__other') {
      return 'iWorkAtSome';
    }
    if (industry?.label?.toLowerCase()?.startsWith('ho')) {
      return 'iWorkAtAn';
    }
    if (['a', 'e', 'i', 'o', 'u'].includes(industry?.label?.toLowerCase()?.substring(0, 1))) {
      return 'iWorkAtAn';
    }
    return 'iWorkAtA';
  }, [industry]);

  return (
    <>
      <Title>{t('yourCompanyTitle')}</Title>
      {showTitle && <SubTitle>{t('yourCompanySubTitle', { step: 3 })}</SubTitle>}

      <Row>
        <Text>{t(prefix)}</Text>
        <SelectField
          name="industryId"
          placeholder={t('choosePlace')}
          options={industriesOptions}
          isMulti={false}
          usePortal
          odd
        />
        {industry && <Text>{t(industry?.category)}</Text>}
      </Row>

      {industryId && (
        <Row>
          <Trans i18nKey="ofNumbersPeople">
            <SelectField name="size" placeholder={t('chooseSize')} options={SIZE_OPTIONS} isMulti={false} usePortal />
          </Trans>
        </Row>
      )}

      {size && (
        <Row>
          <Text>{t('iAm')}</Text>
          <SelectField name="role" placeholder={t('chooseRole')} options={ROLE_OPTIONS} isMulti={false} usePortal odd />
          <Text>.</Text>
        </Row>
      )}

      {/* {role && (
        <Row>
          <Text>{t('myTeamMembers')}</Text>
          <SelectField
            name="members"
            placeholder={t('chooseMembers')}
            options={MEMBERS_OPTIONS}
            isMulti={false}
            usePortal
          />
        </Row>
      )} */}

      {/* {members && (
        <Row>
          <Text>{t('typeOfMembers')}</Text>
          <SelectField
            name="memberTypes"
            placeholder={t('chooseType')}
            options={MEMBERS_TYPES_OPTIONS}
            isMulti={false}
            usePortal
            odd
          />
          {memberTypes && <Text>.</Text>}
        </Row>
      )} */}
    </>
  );
};

export default YourCompany;
